import React from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { NavLinkButton } from '@shared/components/bootstrap';
import { UserRole } from '@shared/types/user_role';

import { Panel } from '@admin/components/helpers/panel';
import { Roles } from '@admin/components/helpers/roles';
import { HistoriesPanel } from '@admin/components/histories/panel';
import { LocationImagesPanel } from '@admin/components/location_images/location_images_panel';
import { OpsAuditResultsPanel } from '@admin/components/ops_audit/audit_results/audit_results_panel';
import { QaQrCodeLink } from '@admin/components/qa_automation/qa_qr_code_link';
import { Spinner } from '@admin/components/spinner';
import { locationURL, warehouseLocationsURL, warehousesURL } from '@admin/config/routes';
import { useTitle } from '@admin/hooks/use_title';
import { History__Subject } from '@admin/schema';

import { useLocationRequest } from './api';
import { LocationPalletsList } from './pallets_list';

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <p>
    <strong>{label}</strong> <span>{children}</span>
  </p>
);

const formatBoolean = (value: boolean) => String(value);

export const LocationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { loading, response, error } = useLocationRequest(id);
  const location = response?.data;

  const title = (() => {
    if (!location) return;
    const warehouse = location.located?.object;
    if (location.located?.type === 'warehouse' && warehouse?.abbreviation) {
      return `${warehouse.abbreviation}:#${location.barcode.value}`;
    } else {
      return `${location.located?.type ?? 'Location'}:#${location.barcode.value}`;
    }
  })();

  useTitle(title);

  if (loading) {
    return <Spinner />;
  }

  if (error || !location) {
    return <div className="alert alert-danger">Location not found.</div>;
  }

  const warehouse = location.located?.object;
  const categoryNames = (location.categories || []).map((category) => category.name).join(', ');

  return (
    <>
      <h1 className="page-header text-overflow">
        <ol className="breadcrumb">
          <li>
            <NavLink to={warehousesURL()}>Warehouses</NavLink>
          </li>
          {warehouse && (
            <li>
              <NavLink to={`/warehouses/${warehouse.id}`}>{warehouse.name}</NavLink>
            </li>
          )}
          {warehouse && (
            <li>
              <NavLink to={warehouseLocationsURL(warehouse.id)}>Locations</NavLink>
            </li>
          )}
          <li className="active">{location.value}</li>
        </ol>
      </h1>

      <Panel>
        <Panel.Header>
          <Panel.Title>Details</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          {warehouse && <DetailRow label="Warehouse">{warehouse.name}</DetailRow>}
          <DetailRow label="Type">{location.type}</DetailRow>
          <DetailRow label="Barcode">
            #{location.barcode.value} <QaQrCodeLink value={location.barcode.value} />
          </DetailRow>
          {location.name && <DetailRow label="Name">#{location.name}</DetailRow>}
          <DetailRow label="Categories">{categoryNames}</DetailRow>
          {location.description && <DetailRow label="Description">{location.description}</DetailRow>}
          {location.aisle && <DetailRow label="Aisle">{location.aisle}</DetailRow>}
          {location.row && <DetailRow label="Row">{location.row}</DetailRow>}
          {location.stack && <DetailRow label="Stack">{location.stack}</DetailRow>}
          {location.level && <DetailRow label="Level">{location.level}</DetailRow>}
          {location.bay && <DetailRow label="Bay">{location.bay}</DetailRow>}
          {location.width != null && <DetailRow label="Width (inches)">{location.width}</DetailRow>}
          {location.depth != null && <DetailRow label="Depth (inches)">{location.depth}</DetailRow>}
          {location.height != null && <DetailRow label="Height (inches)">{location.height}</DetailRow>}
          <DetailRow label="Damaged">{formatBoolean(location.damaged)}</DetailRow>
          <DetailRow label="Inactive">{formatBoolean(location.inactive)}</DetailRow>
          <DetailRow label="Elevation">{location.elevation}</DetailRow>
          {location.capacity != null && <DetailRow label="Capacity">{location.capacity}</DetailRow>}
          <DetailRow label="Is Full">{formatBoolean(location.is_full)}</DetailRow>
          {location.extends_to_location && (
            <DetailRow label="Extends to location">
              <a href={locationURL(location.extends_to_location.id)} className="text-primary">
                #{location.extends_to_location.value}
              </a>{' '}
              <QaQrCodeLink value={location.extends_to_location.value} />
            </DetailRow>
          )}
          {location.directable_kind && <DetailRow label="Directable Kind">{location.directable_kind}</DetailRow>}
        </Panel.Body>
        <Panel.Footer align="right">
          <Roles show={[UserRole.Admin, UserRole.Warehouse]}>
            <Roles show={[UserRole.Admin, UserRole.Manager]}>
              <NavLinkButton kind="primary" to={`/locations/${location.id}/edit`}>
                Edit
              </NavLinkButton>
            </Roles>
          </Roles>
        </Panel.Footer>
      </Panel>

      <LocationPalletsList locationID={String(location.id)} />

      {warehouse?.id && (
        <LocationImagesPanel destinationLocationID={String(location.id)} timezone={warehouse.timezone || ''} />
      )}

      <HistoriesPanel type={History__Subject.Location} id={String(location.id)} />

      <OpsAuditResultsPanel resourceType="Location" resourceID={String(location.id)} />
    </>
  );
};
